/**
 * /admin/proofing — proof workflow center (iter114s.76).
 *
 * Filters the enhanced_orders feed to proof-stage orders only
 * (pending / sent / approved / rejected / revision_requested) so the
 * proofing operator's queue is concentrated. Reuses OrdersTable +
 * OrderManagementModal so all the existing actions (upload proof,
 * approve, reject) work without re-implementation.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from './AdminLayout';
import { OrdersTable } from './OrdersTable';
import { OrderManagementModal } from './OrderManagementModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PROOF_STAGE_STATUSES = new Set([
  'proof_pending', 'proof_sent', 'proof_approved', 'proof_rejected',
  'revision_requested', 'proof_revision_requested', 'awaiting_approval', 'file_review',
]);

const SUBTABS = [
  { id: 'all',                label: 'All proofs' },
  { id: 'proof_pending',      label: 'Awaiting upload' },
  { id: 'proof_sent',         label: 'Awaiting customer' },
  { id: 'proof_rejected',     label: 'Revision requested' },
  { id: 'proof_approved',     label: 'Recently approved' },
];

const AdminProofing = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subtab, setSubtab] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderStatus, setOrderStatus] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/enhanced-orders/`, { withCredentials: true });
      setOrders((res.data || []).filter((o) => PROOF_STAGE_STATUSES.has((o.status || '').toLowerCase())));
    } catch { toast.error('Failed to load proof queue.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const filtered = useMemo(() => (
    subtab === 'all' ? orders : orders.filter((o) => (o.status || '').toLowerCase() === subtab)
  ), [orders, subtab]);

  const openModal = (o) => {
    setSelectedOrder(o);
    setTrackingNumber(o.tracking_number || '');
    setOrderStatus(o.status || '');
    setProofFile(null);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setSelectedOrder(null); };

  const handleUploadProof = async () => {
    if (!proofFile || !selectedOrder) return;
    const fd = new FormData();
    fd.append('order_id', selectedOrder.id);
    fd.append('file', proofFile);
    try {
      await axios.post(`${API_URL}/api/files/upload-proof`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true,
      });
      toast.success('Proof uploaded.'); fetch(); closeModal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed.'); }
  };

  const handleUpdate = async () => {
    if (!selectedOrder) return;
    try {
      await axios.put(`${API_URL}/api/enhanced-orders/${selectedOrder.id}/status`, {
        status: orderStatus, tracking_number: trackingNumber || null,
      }, { withCredentials: true });
      toast.success('Order updated.'); fetch(); closeModal();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed.'); }
  };

  return (
    <AdminLayout>
      <div className="max-w-screen-2xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[#14281D]" data-testid="admin-proofing-heading">Proofing</h1>
          <p className="text-sm text-[#1F3D2C]/70">Active proof-stage orders — pending uploads, customer approval queue, revision requests.</p>
        </header>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-proofing-subtabs">
          {SUBTABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setSubtab(t.id)}
              data-testid={`proofing-subtab-${t.id}`}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
                subtab === t.id
                  ? 'bg-[#2F5D3A] text-white border-[#2F5D3A]'
                  : 'bg-white text-[#14281D] border-[#E5E7EB] hover:border-[#6FBF73]'
              }`}
            >
              {t.label} <span className="opacity-60 ml-1">({orders.filter((o) => t.id === 'all' || (o.status || '').toLowerCase() === t.id).length})</span>
            </button>
          ))}
        </div>
        <OrdersTable orders={filtered} loading={loading} onManageOrder={openModal} />
      </div>
      <OrderManagementModal
        open={showModal} order={selectedOrder} proofFile={proofFile}
        trackingNumber={trackingNumber} orderStatus={orderStatus}
        onClose={closeModal} onProofFileChange={setProofFile}
        onUploadProof={handleUploadProof} onTrackingChange={setTrackingNumber}
        onStatusChange={setOrderStatus} onUpdateStatus={handleUpdate}
      />
    </AdminLayout>
  );
};

export default AdminProofing;
